import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { useSessionContext } from '../../contexts/SessionContext';
import type { SourceDoc, SummaryResponse } from '../../types';

interface SourceResponse {
  cite_key: string;
  title?: string;
  url?: string;
  date?: string;
  author?: string;
  organization?: string;
}

interface MalpediaResponse {
  cite_key: string;
  families: { id: string; name: string }[];
  actors: { id: string; name: string }[];
}

/** Derive metadata (title, url, etc.) from session snippets for a cite_key. */
function useSessionMeta(citeKey: string | undefined) {
  const { activeSession } = useSessionContext();
  if (!citeKey || !activeSession) return null;

  for (const msg of activeSession.messages) {
    if (!msg.sources) continue;
    for (const src of msg.sources) {
      if (src.cite_key === citeKey) return src;
    }
  }
  return null;
}

export default function SourceViewPage() {
  const { citeKey } = useParams<{ citeKey: string }>();
  const navigate = useNavigate();
  const sessionMeta = useSessionMeta(citeKey);

  // Always fetch metadata from API (needed for org/author/date not in snippets)
  const { data, isLoading, error } = useQuery<SourceResponse>({
    queryKey: ['source', citeKey],
    queryFn: () => apiFetch<SourceResponse>(`/api/fastapi/documents/${encodeURIComponent(citeKey || '')}`),
    enabled: !!citeKey,
  });

  // Fetch AI summary
  const { data: summaryData } = useQuery<SummaryResponse>({
    queryKey: ['source-summary', citeKey],
    queryFn: () => apiFetch<SummaryResponse>(`/api/fastapi/documents/${encodeURIComponent(citeKey || '')}/summary`),
    enabled: !!citeKey,
  });

  // Fetch Malpedia attribution
  const { data: malpediaData } = useQuery<MalpediaResponse>({
    queryKey: ['source-malpedia', citeKey],
    queryFn: () => apiFetch<MalpediaResponse>(`/api/fastapi/documents/${encodeURIComponent(citeKey || '')}/malpedia`),
    enabled: !!citeKey,
  });

  // Derive metadata: session snippets first, then API response
  const title = sessionMeta?.title ?? data?.title;
  const url = sessionMeta?.url ?? data?.url;
  const organization = data?.organization;
  const author = data?.author;
  const date = data?.date;

  if (!sessionMeta && isLoading) {
    return <div className="malpedia-source-view malpedia-source-view--loading">Loading...</div>;
  }
  if (!sessionMeta && error && !data) {
    const msg = error instanceof Error ? error.message : 'Failed to load source';
    return (
      <div className="malpedia-source-view malpedia-source-view--error">
        <button className="malpedia-source-back" onClick={() => navigate(-1)}>&larr; Back</button>
        <p>Failed to load source: {msg}</p>
      </div>
    );
  }
  if (!sessionMeta && !data) return null;

  const summary = summaryData?.summary;
  const hasMalpedia = (malpediaData?.families?.length ?? 0) > 0 || (malpediaData?.actors?.length ?? 0) > 0;
  const hasAiTags = !!(summaryData?.topic || (summaryData?.actors?.length ?? 0) > 0 || (summaryData?.families?.length ?? 0) > 0);

  return (
    <div className="malpedia-source-view">
      <button className="malpedia-source-back" onClick={() => navigate(-1)}>&larr; Back</button>

      {/* ── Header block ── */}
      <header className="malpedia-source-header">
        <div className="malpedia-source-meta-top">
          <span className="malpedia-source-cite">{citeKey}</span>
          {date && <span className="malpedia-source-date">{date}</span>}
        </div>
        <h1>{title ?? citeKey}</h1>
        <div className="malpedia-source-meta-bottom">
          {organization && <span>{organization}</span>}
          {organization && url && <span className="malpedia-source-dot">&middot;</span>}
          {url && (
            <a href={url} target="_blank" rel="noreferrer" className="malpedia-source-original-link">
              Read original &#x2197;
            </a>
          )}
        </div>
      </header>

      {/* ── Malpedia attribution bar ── */}
      {hasMalpedia && (
        <section className="malpedia-source-attribution">
          <span className="malpedia-source-attribution-label">MALPEDIA</span>
          <div className="malpedia-source-attribution-tags">
            {malpediaData!.families.map((f) => (
              <a key={f.id} href={`https://malpedia.caad.fkie.fraunhofer.de/details/${f.id}`}
                 target="_blank" rel="noreferrer"
                 className="malpedia-source-attribution-tag">{f.name}</a>
            ))}
            {malpediaData!.actors.map((a) => (
              <a key={a.id} href={`https://malpedia.caad.fkie.fraunhofer.de/actor/${a.id}`}
                 target="_blank" rel="noreferrer"
                 className="malpedia-source-attribution-tag">{a.name}</a>
            ))}
          </div>
        </section>
      )}

      {/* ── AI Summary section ── */}
      <section className="malpedia-source-summary">
        {summary ? (
          <>
            <div className="malpedia-source-ai-label">&#x2728; AI SUMMARY</div>
            <div className="malpedia-source-summary-text">{summary}</div>
            {hasAiTags && (
              <div className="malpedia-source-summary-tags">
                {summaryData!.topic && (
                  <span className="malpedia-source-ai-tag--topic">{summaryData!.topic}</span>
                )}
                {summaryData!.families?.map((f) => (
                  <span key={f} className="malpedia-source-ai-tag--entity">{f}</span>
                ))}
                {summaryData!.actors?.map((a) => (
                  <span key={a} className="malpedia-source-ai-tag--entity">{a}</span>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="malpedia-source-summary-missing">
            Summary not yet available for this document.
          </div>
        )}
      </section>
    </div>
  );
}
